import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Eye, EyeOff, Plus, Save, Trash2, Cloud } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { getDREDetailed, listNamedScenarios, createNamedScenario, deleteNamedScenario } from '../../services/financialPlanningService';

const BRAND_GREEN = '#064e3b';

const formatCurrency = (v, currency = 'BRL') => {
  if (v == null || v === 0) return '—';
  const neg = v < 0;
  const abs = Math.abs(v);
  if (currency === 'USD') {
    return `${neg ? '-' : ''}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `${neg ? '-' : ''}R$${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const formatMonth = (m) => {
  const [y, mo] = m.split('-');
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${months[parseInt(mo)-1]}/${y.slice(2)}`;
};

const AccountRow = ({ account, months, viewMode, currency = 'BRL' }) => {
  const isGroup = account.is_group;
  const isTopLevel = !account.parent_code;
  const indent = account.parent_code ? (account.code.split('.').length - 1) * 16 : 0;
  const isAzure = !!account.is_azure;
  const creditCovered = !!account.azure_credit_covered;

  // Style based on level
  const rowClass = isTopLevel && isGroup
    ? 'bg-gray-100 font-bold border-t-2 border-gray-300'
    : isGroup
    ? 'bg-gray-50 font-semibold'
    : 'hover:bg-blue-50/30';

  const textColor = account.nature === 'debit' ? 'text-red-700' : 'text-gray-900';

  return (
    <tr className={`border-b ${rowClass}`}>
      <td className="px-3 py-1.5 whitespace-nowrap sticky left-0 bg-inherit" style={{ paddingLeft: `${12 + indent}px` }}>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400 font-mono w-10">{account.code}</span>
          <span className={`text-xs ${isGroup ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
            {account.name}
          </span>
          {isAzure && (
            <span className="flex items-center gap-0.5 text-[9px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded ml-1" title="Custo Azure">
              <Cloud size={8} />
              {creditCovered ? ' crédito' : ' caixa'}
            </span>
          )}
        </div>
      </td>
      {months.map(month => {
        const vals = account.values[month] || { real: 0, projected: 0 };
        // When USD mode and we have real_usd, use it; else convert BRL÷usd_brl
        const useUsd = currency === 'USD';
        const usdBrl = vals.usd_brl || 5.70;
        const realBrl = vals.real || 0;
        const realDisp = useUsd
          ? (vals.real_usd != null ? vals.real_usd : realBrl / usdBrl)
          : realBrl;
        const projBrl = vals.projected || 0;
        const projDisp = useUsd ? projBrl / usdBrl : projBrl;
        const showVal = viewMode === 'real' ? realDisp : viewMode === 'projected' ? projDisp : projDisp;
        return (
          <td key={month} className={`px-2 py-1.5 text-right font-mono text-xs ${textColor}`}>
            {viewMode === 'both' ? (
              <div>
                <div className="text-gray-900">{formatCurrency(projDisp, currency)}</div>
                {realBrl !== 0 && <div className="text-[10px] text-blue-600">{formatCurrency(realDisp, currency)}</div>}
              </div>
            ) : (
              formatCurrency(showVal, currency)
            )}
          </td>
        );
      })}
    </tr>
  );
};

const FinancialDREDetailedTab = () => {
  const { lang } = useGovernanceLang();
  const L = {
    pt: {
      title: 'DRE Detalhada',
      both: 'Real + Projetado', projected: 'Só Projetado', real: 'Só Realizado',
      base: 'Base', optimistic: 'Otimista', pessimistic: 'Pessimista',
      monthly: 'Mensal', quarterly: 'Trimestral', semiannual: 'Semestral', annual: 'Anual',
      mo6: '6 meses', mo12: '12 meses', mo24: '24 meses', mo36: '36 meses',
      colAccount: 'Conta',
      legendProjected: 'Projetado', legendReal: 'Realizado',
      confirmDelete: 'Excluir cenário?',
      scenarioPlaceholder: 'Nome do cenário (ex: Investimento 250k)',
      create: 'Criar',
      noData: 'Nenhum lançamento financeiro encontrado. Adicione receitas e despesas nas abas correspondentes.',
      noRevenue: (tab) => <>Nenhuma receita lançada ainda. O DRE exibe apenas despesas reais. Adicione receitas na aba <strong>{tab}</strong> para ver o DRE completo.</>,
      revenueTab: 'Receita',
      footer: 'Tributos estimados à alíquota Simples Nacional (9,65%). Meses futuros usam projeção de lançamentos recorrentes.',
      realData: '✓ Dados reais de receitas e despesas',
    },
    en: {
      title: 'Detailed P&L',
      both: 'Actual + Projected', projected: 'Projected Only', real: 'Actual Only',
      base: 'Base', optimistic: 'Optimistic', pessimistic: 'Pessimistic',
      monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semi-annual', annual: 'Annual',
      mo6: '6 months', mo12: '12 months', mo24: '24 months', mo36: '36 months',
      colAccount: 'Account',
      legendProjected: 'Projected', legendReal: 'Actual',
      confirmDelete: 'Delete scenario?',
      scenarioPlaceholder: 'Scenario name (e.g. Investment 250k)',
      create: 'Create',
      noData: 'No financial entries found. Add revenues and expenses in the corresponding tabs.',
      noRevenue: (tab) => <>No revenue entries yet. The P&L shows only real expenses. Add revenues in the <strong>{tab}</strong> tab to see the full P&L.</>,
      revenueTab: 'Revenue',
      footer: 'Taxes estimated at Simples Nacional rate (9.65%). Future months use recurring entry projections.',
      realData: '✓ Real revenue and expense data',
    },
  }[lang] || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [scenario, setScenario] = useState('base');
  const [viewMode, setViewMode] = useState('both'); // 'real', 'projected', 'both'
  const [periodView, setPeriodView] = useState('monthly'); // monthly, quarterly, semiannual, annual
  const [scenarios, setScenarios] = useState([]);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [currency, setCurrency] = useState(
    () => localStorage.getItem('finops_currency') || 'BRL'
  );
  const handleCurrencyToggle = (c) => { setCurrency(c); localStorage.setItem('finops_currency', c); };

  const loadScenarios = useCallback(async () => {
    try {
      const res = await listNamedScenarios();
      setScenarios(res?.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDREDetailed(months, scenario, true);
      setData(res?.data || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [months, scenario]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);
  useEffect(() => { load(); }, [load]);

  const handleCreateScenario = async () => {
    if (!newScenarioName.trim()) return;
    const key = newScenarioName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    await createNamedScenario({ name: newScenarioName.trim(), scenario_key: key });
    setNewScenarioName('');
    setShowNewForm(false);
    loadScenarios();
    setScenario(key);
  };

  const handleDeleteScenario = async (id, key) => {
    if (!window.confirm(L.confirmDelete)) return;
    await deleteNamedScenario(id);
    loadScenarios();
    if (scenario === key) setScenario('base');
  };

  if (loading) {
    return <div className="h-96 bg-white rounded-xl animate-pulse" />;
  }

  if (!data || !data.accounts) {
    return <div className="text-gray-500 text-center py-12">{L.noData}</div>;
  }

  // Detect if revenue is all-zero (no real revenue lançada ainda)
  const hasRealRevenue = data.accounts?.some(acc =>
    Object.values(acc.values || {}).some(v => v.real > 0)
  );

  const monthList = data.months || [];
  const aggregatePeriods = (monthsArr) => {
    if (periodView === 'monthly') return monthsArr.map(m => ({ key: m, label: formatMonth(m), months: [m] }));
    const periods = [];
    if (periodView === 'quarterly') {
      for (let i = 0; i < monthsArr.length; i += 3) {
        const slice = monthsArr.slice(i, i + 3);
        periods.push({ key: `Q${Math.floor(i/3)+1}`, label: `T${Math.floor(i/3)+1} ${slice[0]?.split('-')[0]}`, months: slice });
      }
    } else if (periodView === 'semiannual') {
      for (let i = 0; i < monthsArr.length; i += 6) {
        const slice = monthsArr.slice(i, i + 6);
        periods.push({ key: `S${Math.floor(i/6)+1}`, label: `S${Math.floor(i/6)+1} ${slice[0]?.split('-')[0]}`, months: slice });
      }
    } else if (periodView === 'annual') {
      const byYear = {};
      monthsArr.forEach(m => { const y = m.split('-')[0]; if (!byYear[y]) byYear[y] = []; byYear[y].push(m); });
      Object.entries(byYear).forEach(([y, ms]) => periods.push({ key: y, label: y, months: ms }));
    }
    return periods;
  };

  const periods = aggregatePeriods(monthList);

  // Build aggregated accounts
  const aggregatedAccounts = data.accounts.map(acc => ({
    ...acc,
    is_azure: acc.is_azure,
    azure_credit_covered: acc.azure_credit_covered,
    values: Object.fromEntries(periods.map(p => [p.key, {
      real:      p.months.reduce((s, m) => s + (acc.values[m]?.real || 0), 0),
      projected: p.months.reduce((s, m) => s + (acc.values[m]?.projected || 0), 0),
      real_usd:  p.months.reduce((s, m) => s + (acc.values[m]?.real_usd || 0), 0),
      usd_brl:   p.months.length > 0 ? (acc.values[p.months[p.months.length - 1]]?.usd_brl || 5.70) : 5.70,
    }]))
  }));

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} style={{ color: BRAND_GREEN }} />
          <h2 className="text-lg font-bold text-gray-900">{L.title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {/* Currency toggle */}
          <div className="flex items-center border rounded overflow-hidden text-xs">
            <button
              onClick={() => handleCurrencyToggle('BRL')}
              className={`px-2 py-1 font-bold transition-colors ${currency === 'BRL' ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >R$</button>
            <button
              onClick={() => handleCurrencyToggle('USD')}
              className={`px-2 py-1 font-bold transition-colors ${currency === 'USD' ? 'bg-blue-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >US$</button>
          </div>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} className="text-xs border rounded px-2 py-1">
            <option value="both">{L.both}</option>
            <option value="projected">{L.projected}</option>
            <option value="real">{L.real}</option>
          </select>
          <select value={scenario} onChange={e => setScenario(e.target.value)} className="text-xs border rounded px-2 py-1 font-semibold">
            <option value="base">{L.base}</option>
            <option value="optimistic">{L.optimistic}</option>
            <option value="pessimistic">{L.pessimistic}</option>
            {scenarios.filter(s => !['base','optimistic','pessimistic'].includes(s.scenario_key)).map(s => (
              <option key={s.id} value={s.scenario_key}>{s.name}</option>
            ))}
          </select>
          <button onClick={() => setShowNewForm(!showNewForm)} className="text-xs px-2 py-1 border rounded hover:bg-gray-100">
            <Plus size={12} />
          </button>
          <select value={periodView} onChange={e => setPeriodView(e.target.value)} className="text-xs border rounded px-2 py-1">
            <option value="monthly">{L.monthly}</option>
            <option value="quarterly">{L.quarterly}</option>
            <option value="semiannual">{L.semiannual}</option>
            <option value="annual">{L.annual}</option>
          </select>
          <select value={months} onChange={e => setMonths(parseInt(e.target.value))} className="text-xs border rounded px-2 py-1">
            <option value={6}>{L.mo6}</option>
            <option value={12}>{L.mo12}</option>
            <option value={24}>{L.mo24}</option>
            <option value={36}>{L.mo36}</option>
          </select>
        </div>
      </div>

      {/* Revenue warning banner */}
      {!hasRealRevenue && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <span className="text-lg">⚠️</span>
          <span>{L.noRevenue(L.revenueTab)}</span>
        </div>
      )}

      {/* New Scenario Form */}
      {showNewForm && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <input value={newScenarioName} onChange={e => setNewScenarioName(e.target.value)}
            placeholder={L.scenarioPlaceholder}
            className="flex-1 text-sm border rounded px-3 py-1.5"
            onKeyDown={e => e.key === 'Enter' && handleCreateScenario()} />
          <button onClick={handleCreateScenario} className="text-xs px-3 py-1.5 rounded text-white font-bold" style={{ backgroundColor: BRAND_GREEN }}>
            {L.create}
          </button>
          {scenarios.filter(s => !['base','optimistic','pessimistic'].includes(s.scenario_key)).map(s => (
            <span key={s.id} className="text-[10px] bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
              {s.name}
              <button onClick={() => handleDeleteScenario(s.id, s.scenario_key)} className="text-red-500 hover:text-red-700"><Trash2 size={10} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Legend */}
      {viewMode === 'both' && (
        <div className="flex items-center gap-4 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-900 rounded-full" /> {L.legendProjected}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-600 rounded-full" /> {L.legendReal}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-bold text-gray-600 sticky left-0 bg-gray-50 min-w-[280px]">
                {L.colAccount}
              </th>
              {periods.map(p => (
                <th key={p.key} className="px-2 py-2.5 text-right text-xs font-bold text-gray-600 whitespace-nowrap min-w-[90px]">
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {aggregatedAccounts.map(acc => (
              <AccountRow key={acc.code} account={acc} months={periods.map(p => p.key)} viewMode={viewMode} currency={currency} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer info */}
      <div className="text-[10px] text-gray-400 text-right flex items-center justify-between">
        <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-medium">{L.realData}</span>
        <span>{L.footer}</span>
      </div>
    </div>
  );
};

export default FinancialDREDetailedTab;
